import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { tokens } from "../../theme";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import ProductionQuantityLimitsOutlinedIcon from "@mui/icons-material/ProductionQuantityLimitsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import BarChart from "../../components/BarChart";
import StatBox from "../../components/StatBox";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState({
    summary: {},
    recent: { sales: [], stockouts: [], returns: [] },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.id;
        if (!userId) throw new Error("User not found in localStorage");

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dashboard/sales/${userId}`
        );
        if (!res.ok) throw new Error("Failed to fetch dashboard data");
        const json = await res.json();

        setData({
          summary: {
            totalSalesAmount: json.metrics.totalSalesAmount,
            totalReturnCount: json.metrics.totalReturnCount,
            totalStockoutItems: json.metrics.totalStockoutItems,
            itemsSoldCount: json.metrics.itemsSoldCount,
          },
          recent: {
            sales: json.recentSales || [],
            stockouts: json.recentStockouts || [],
            returns: json.recentReturns || [],
          },
        });
      } catch (err) {
        console.error(err);
        setData({
          summary: {},
          recent: { sales: [], stockouts: [], returns: [] },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const barData = useMemo(() => {
    const salesRows = data.recent.sales || [];
    const sums = new Map();
    for (const s of salesRows) {
      const name = s?.SalesItems?.[0]?.Item?.name || "Unknown";
      const amt = Number.parseFloat(s?.SalesItems?.[0]?.quantity || 0);
      sums.set(name, (sums.get(name) || 0) + (Number.isFinite(amt) ? amt : 0));
    }
    return Array.from(sums.entries()).map(([name, total]) => ({
      name,
      salesPrice: total,
    }));
  }, [data]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const { summary, recent } = data;

  return (
    <Box m={{ xs: "10px", md: "20px" }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
        <Button
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
          }}
        >
          <DownloadOutlinedIcon sx={{ mr: 1 }} />
          Download
        </Button>
      </Box>

      {/* Scrollable container */}
      <Box sx={{ overflowX: { xs: "auto", md: "visible" } }}>
        <Box sx={{ minWidth: { xs: 1100, md: "auto" } }}>
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gridAutoRows="140px"
            gap="20px"
          >
            {/* Stat Boxes */}
            <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
              <StatBox
                title={summary.totalSalesAmount?.toLocaleString() || 0}
                subtitle="Total Sales Amount"
                progress="0.7"
                increase="ETB"
                icon={
                  <PointOfSaleIcon
                    sx={{ color: colors.greenAccent[600], fontSize: 28 }}
                  />
                }
              />
            </Box>
            <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
              <StatBox
                title={summary.itemsSoldCount || 0}
                subtitle="Total Items Sold"
                progress="0.6"
                increase=""
                icon={
                  <Inventory2OutlinedIcon
                    sx={{ color: colors.greenAccent[600], fontSize: 28 }}
                  />
                }
              />
            </Box>
            <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
              <StatBox
                title={summary.totalStockoutItems || 0}
                subtitle="Stockouts"
                progress="0.5"
                increase=""
                icon={
                  <ProductionQuantityLimitsOutlinedIcon
                    sx={{ color: colors.greenAccent[600], fontSize: 28 }}
                  />
                }
              />
            </Box>
            <Box gridColumn="span 3" backgroundColor={colors.primary[400]}>
              <StatBox
                title={summary.totalReturnCount || 0}
                subtitle="Returns"
                progress="0.4"
                increase=""
                icon={
                  <AssignmentReturnOutlinedIcon
                    sx={{ color: colors.greenAccent[600], fontSize: 28 }}
                  />
                }
              />
            </Box>

            {/* Line Chart */}
            <Box
              gridColumn="span 8"
              gridRow="span 2"
              backgroundColor={colors.primary[400]}
            >
              <Box px={3} py={2}>
                <Typography variant="h6" color={colors.grey[100]}>
                  Revenue Generated
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={colors.greenAccent[500]}
                >
                  {summary.totalSalesAmount?.toLocaleString() || 0} ETB
                </Typography>
              </Box>
              <Box height={250}>
                <LineChart isDashboard />
              </Box>
            </Box>

            {/* Bar Chart */}
            <Box
              gridColumn="span 4"
              gridRow="span 2"
              backgroundColor={colors.primary[400]}
            >
              <Typography variant="h6" sx={{ p: 2 }} color={colors.grey[100]}>
                Items Sold
              </Typography>
              <Box height={250} mt={-2}>
                <BarChart isDashboard data={barData} />
              </Box>
            </Box>

            {/* Recent Sales */}
            <Box
              gridColumn="span 4"
              gridRow="span 2"
              backgroundColor={colors.primary[400]}
              overflow="auto"
            >
              <Typography variant="h6" sx={{ p: 2 }} color={colors.grey[100]}>
                Recent Sales
              </Typography>
              {recent.sales.map((sale) => (
                <Box
                  key={sale.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={2}
                  borderBottom={`2px solid ${colors.primary[500]}`}
                >
                  <Box>
                    <Typography
                      color={colors.greenAccent[500]}
                      fontWeight={600}
                    >
                      {sale.Customer?.name || "Unknown"}
                    </Typography>
                    <Typography color={colors.grey[100]}>
                      {sale.SalesItems?.[0]?.Item?.name || "Unknown"} ×{" "}
                      {sale.SalesItems?.[0]?.quantity || 0}
                    </Typography>
                  </Box>
                  <Typography color={colors.grey[100]}>
                    {new Date(sale.salesDate).toLocaleDateString()}
                  </Typography>
                  <Box
                    backgroundColor={colors.greenAccent[500]}
                    px={1.5}
                    py={0.7}
                    borderRadius="4px"
                  >
                    {parseFloat(sale.totalPrice).toLocaleString()}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Recent Stockouts */}
            <Box
              gridColumn="span 4"
              gridRow="span 2"
              backgroundColor={colors.primary[400]}
              overflow="auto"
            >
              <Typography variant="h6" sx={{ p: 2 }} color={colors.grey[100]}>
                Recent Stockouts
              </Typography>
              {recent.stockouts.map((st) => (
                <Box
                  key={st.id}
                  p={2}
                  borderBottom={`2px solid ${colors.primary[500]}`}
                >
                  <Typography color={colors.greenAccent[500]} fontWeight={600}>
                    {st.Warehouse?.name || "Unknown"}
                  </Typography>
                  <Typography color={colors.grey[100]}>
                    {st.StockoutItems?.[0]?.Item?.name || "Unknown"} —{" "}
                    {st.StockoutItems?.[0]?.amount} pcs
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Recent Returns */}
            <Box
              gridColumn="span 4"
              gridRow="span 2"
              backgroundColor={colors.primary[400]}
              overflow="auto"
            >
              <Typography variant="h6" sx={{ p: 2 }} color={colors.grey[100]}>
                Recent Returns
              </Typography>
              {recent.returns.map((r) => (
                <Box
                  key={r.id}
                  p={2}
                  borderBottom={`2px solid ${colors.primary[500]}`}
                >
                  <Typography color={colors.greenAccent[500]} fontWeight={600}>
                    {r.Warehouse?.name || "Unknown"}
                  </Typography>
                  <Typography color={colors.grey[100]}>
                    Reason: {r.reason || "N/A"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
